import { EyeOff, DoorOpen, Egg, UtensilsCrossed, Building, Repeat } from "lucide-react";
import { ChapterTag, MaskedLines, FadeUp } from "./Reveal";
import { BookButton } from "./CTA";

const SIGNS = [
  { icon: EyeOff, title: "Hidden infestation", text: "For every pest you see, more are often nesting out of sight." },
  { icon: DoorOpen, title: "Entry points", text: "Gaps, drains and cracks invite pests back unless identified." },
  { icon: Egg, title: "Breeding areas", text: "Damp, undisturbed corners let populations rebuild quietly." },
  { icon: UtensilsCrossed, title: "Food & water sources", text: "Accessible food and moisture sustain the problem." },
  { icon: Building, title: "Structural access", text: "Termites and rodents exploit weaknesses in the building itself." },
  { icon: Repeat, title: "Recurring activity", text: "Treating only what you see means the cycle repeats." },
];

const PESTS = ["Cockroaches", "Termites", "Bed Bugs", "Rodents", "Mosquitoes", "Ants", "Flies"];

export const Problem = () => (
  <section data-testid="problem-section" className="py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <ChapterTag index="01" label="The Problem" />
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 mt-10">
        <div>
          <h2 data-testid="problem-headline" className="font-display font-semibold text-charcoal tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLines lines={[<>One pest is usually a sign of a <span className="italic text-wine">bigger problem.</span></>]} />
          </h2>
          <FadeUp delay={0.3} className="mt-7 max-w-lg">
            <p className="text-stone2 leading-relaxed">
              Spraying the pest you can see rarely solves the one you can't. A visible cockroach, a single termite tube, one rodent — each points to conditions behind your walls, under your floors and around your property that allow pests to settle and multiply.
            </p>
          </FadeUp>
          <FadeUp delay={0.45} className="mt-9">
            <BookButton testid="problem-book-btn" />
          </FadeUp>
          <FadeUp delay={0.55} className="mt-10 flex flex-wrap gap-2">
            {PESTS.map((p) => (
              <span key={p} data-testid={`problem-pest-${p.toLowerCase().replace(/\s/g, "-")}`} className="border border-border px-4 py-2 text-[12px] font-semibold tracking-wide text-charcoal/70">
                {p}
              </span>
            ))}
          </FadeUp>
        </div>
        <div className="grid sm:grid-cols-2 gap-px bg-border border border-border">
          {SIGNS.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.07} className="bg-ivory p-7 lg:p-8">
              <s.icon className="h-5 w-5 text-wine mb-4" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-semibold text-charcoal mb-1.5">{s.title}</h3>
              <p className="text-sm text-stone2 leading-relaxed">{s.text}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </div>
  </section>
);
